import { z } from 'zod';
import { dataHoraSchema, paginacaoSchema, respostaListaSchema, uuidSchema } from './comum.ts';
import { senhaSchema, temPermissao } from './auth.ts';

/**
 * Console de operação — a equipe que opera o software.
 *
 * Plano de acesso **separado** do de tenant: identidade própria, token próprio
 * e permissões próprias, todas no prefixo `sistema.`. Nenhuma permissão daqui
 * abre rota de cliente, e nenhuma permissão de cliente abre rota daqui.
 *
 * Essa separação é o controle principal desta funcionalidade. Um operador que
 * pudesse assumir contexto de tenant leria dados de qualquer cliente sem deixar
 * rastro no `audit_log` daquele cliente.
 */

export const perfilOperadorSchema = z.enum(['OPERACAO', 'SUPORTE']);
export type PerfilOperador = z.infer<typeof perfilOperadorSchema>;

/**
 * Permissões por perfil.
 *
 * SUPORTE consulta e não altera: é o perfil de quem atende chamado, e a maior
 * parte do atendimento não precisa de escrita. Dar escrita "porque é mais
 * prático" transforma todo atendimento num risco de suspensão acidental de
 * conta em produção.
 */
export const PERMISSOES_POR_PERFIL_OPERADOR: Readonly<Record<PerfilOperador, readonly string[]>> = {
  OPERACAO: ['sistema.*'],
  // `sistema.propria.*` é a credencial do próprio operador (segundo fator):
  // todo perfil tem, porque todo operador precisa ativá-lo.
  SUPORTE: ['sistema.conta.ver', 'sistema.operador.ver', 'sistema.propria.*'],
} as const;

export function permissoesDoPerfil(perfil: PerfilOperador): string[] {
  return [...PERMISSOES_POR_PERFIL_OPERADOR[perfil]];
}

export function operadorPode(perfil: PerfilOperador, permissao: string): boolean {
  return temPermissao(permissoesDoPerfil(perfil), permissao);
}

export const loginOperadorSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  senha: z.string().min(1),
  /** Código do aplicativo (6 dígitos) ou de recuperação, como em `loginSchema`. */
  totp: z.string().trim().min(6).max(20).optional(),
});

export const loginOperadorRespostaSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int(),
  operador: z.object({
    id: uuidSchema,
    nome: z.string(),
    email: z.string().email(),
    perfil: perfilOperadorSchema,
    /**
     * O segundo fator ainda não foi ativado. Para operador ele é obrigatório,
     * então o console mostra a tela de segurança e nada mais até isso mudar.
     */
    mfaPendente: z.boolean(),
  }),
});

export type LoginOperadorResposta = z.infer<typeof loginOperadorRespostaSchema>;

export type SessaoOperador = LoginOperadorResposta['operador'];

/** Contas do sistema, na visão do console. */
export const contaSistemaSchema = z.object({
  id: uuidSchema,
  nome: z.string(),
  ativo: z.boolean(),
  /** Quantos usuários ativos a conta tem. Zero indica conta sem quem entre. */
  usuariosAtivos: z.number().int(),
  criadaEm: dataHoraSchema,
  atualizadaEm: dataHoraSchema,
});

export type ContaSistema = z.infer<typeof contaSistemaSchema>;

export const listaContasSchema = respostaListaSchema(contaSistemaSchema);
export type ListaContas = z.infer<typeof listaContasSchema>;

export const listarContasQuerySchema = paginacaoSchema.extend({
  ativo: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
});

export type ListarContasQuery = z.infer<typeof listarContasQuerySchema>;

/**
 * Provisionamento de conta.
 *
 * Criar a conta e o primeiro administrador é **uma operação só**. Separar as
 * duas deixaria existir, entre um passo e outro, uma conta sem ninguém que
 * consiga entrar nela — e quem descobre isso é o cliente, no primeiro acesso.
 */
export const provisionarContaSchema = z.object({
  nome: z.string().trim().min(2, 'Informe a razão social da conta.').max(200),
  administrador: z.object({
    nome: z.string().trim().min(2, 'Informe o nome do administrador.').max(200),
    email: z.string().trim().toLowerCase().email('E-mail inválido.').max(200),
    senha: senhaSchema,
  }),
});

export type ProvisionarConta = z.infer<typeof provisionarContaSchema>;

/**
 * A resposta do provisionamento: a conta e o administrador, e nada mais.
 *
 * Havia um `planoDeContas` aqui, com quantas contas o provisionamento tinha
 * instalado. Ele saiu junto com a instalação (`.spec/015`, decisão 38): o
 * provisionamento não cria cadastro nenhum, e um campo que responderia sempre a
 * mesma coisa é pior que a ausência dele — quem lesse a resposta concluiria que
 * a instalação falhou.
 */
export const provisionarContaRespostaSchema = z.object({
  conta: contaSistemaSchema,
  administrador: z.object({ id: uuidSchema, email: z.string() }),
});

export type ProvisionarContaResposta = z.infer<typeof provisionarContaRespostaSchema>;

export const renomearContaSchema = z.object({
  nome: z.string().trim().min(2, 'Informe a razão social da conta.').max(200),
});

/**
 * Suspensão exige motivo.
 *
 * A ação tira uma empresa inteira do ar. Sem o motivo registrado, a pergunta
 * "por que a conta X está suspensa?" só se responde perguntando à pessoa que
 * apertou o botão — se ela ainda estiver na equipe.
 */
export const suspenderContaSchema = z.object({
  motivo: z.string().trim().min(5, 'Descreva o motivo da suspensão.').max(500),
});

export type SuspenderConta = z.infer<typeof suspenderContaSchema>;

export const ROTULO_PERFIL_OPERADOR: Readonly<Record<PerfilOperador, string>> = {
  OPERACAO: 'Operação',
  SUPORTE: 'Suporte',
};
